import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, ButtonGroup } from './FormElements';
import { asaasService, sgfService } from '../services/api';

const FormContainer = styled.div`
  width: 100%;
`;

const Title = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.lightText};
  text-align: center;
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: ${props => props.highlight ? 'rgba(255, 127, 0, 0.08)' : props.theme.colors.lightBackground};
  border: 2px solid ${props => props.highlight ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => props.selected && `
    box-shadow: 0 8px 24px rgba(255, 127, 0, 0.25);
    border-color: ${props.theme.colors.primary};
    background: rgba(255, 127, 0, 0.12);
  `}

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 12px rgba(255, 127, 0, 0.15);
    transform: scale(1.2);
  }
`;

const BestOfferBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PlanType = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.lightText};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const PlanInstallment = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const PlanTotal = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.lightText};
  margin-top: 0.25rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  flex: 1;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  line-height: 1.5;

  &:before {
    content: "✓";
    color: ${props => props.theme.colors.primary};
    font-weight: bold;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: none;
  background: ${props => props.selected ? props.theme.colors.primary : 'rgba(255, 127, 0, 0.2)'};
  color: ${props => props.selected ? 'white' : props.theme.colors.primary};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StartupPlansScreen = ({ formData, updateFormData, nextStep, prevStep }) => {
  const [selectedPlan, setSelectedPlan] = useState(formData.selected_plan || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    updateFormData({ selected_plan: plan });
    setIsProcessing(true);
    try {
      // Primeiro: enviar os dados da filiada ao SGF incluindo a descrição do plano em 'observacoes'
      const planDescription = plan === 'monthly'
        ? 'Plano: Anual (12x R$ 99,90) - Total R$ 1.198,80/ano'
        : 'Plano: Anual à vista (R$ 990,00)';

      const observacoes = `${formData.observacoes || ''}`.trim()
        ? `${formData.observacoes}\n${planDescription}`
        : planDescription;

      console.log('Enviando filiada ao SGF com observacoes:', observacoes);

      await sgfService.sendFiliada({ ...formData, observacoes });

      // Mostrar toast de sucesso breve
      setToastMessage('Cadastro enviado com sucesso');
      setTimeout(() => setToastMessage(''), 3000);

      // Em seguida, prosseguir com o fluxo de pagamento Asaas
      // Recuperar ID do cliente do localStorage
      const customerId = localStorage.getItem('asaas_customer_id') || formData.asaas_customer_id;
      
      if (!customerId) {
        throw new Error('ID do cliente não encontrado. Por favor, tente novamente.');
      }

      // Obter URL base para callback
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/success`;

      let paymentData;

      if (plan === 'monthly') {
        // Plano parcelado 12x R$ 99,90
        paymentData = await asaasService.createInstallmentPayment(customerId, 1198.80, 12, successUrl);
      } else if (plan === 'annual') {
        // Plano anual à vista R$ 990,00
        paymentData = await asaasService.createSinglePayment(customerId, 990.00, successUrl);
      }

      // Redirecionar para a URL de pagamento do Asaas
      if (paymentData && paymentData.invoiceUrl) {
        console.log('Redirecionando para checkout:', paymentData.invoiceUrl);
        window.location.href = paymentData.invoiceUrl;
      } else {
        throw new Error('URL de pagamento não foi gerada. Por favor, tente novamente.');
      }

    } catch (error) {
      console.error('Erro ao processar pagamento ou enviar ao SGF:', error);
      alert(`Erro: ${error.message}`);
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPlan) {
      nextStep();
    }
  };

  const benefits = [
    'Plano anual com pagamentos mensais',
    'Todos os benefícios exclusivos',
    'Acesso a empresários experientes',
    'Representação institucional',
    'Participação no Jantar Empresarial EPTEC durante o GoRN',
    'Participação no jantar de confraternização de final de ano'
  ];

  return (
    <FormContainer>
      {/* Toast simples no canto superior direito */}
      {toastMessage && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#10b981', color: 'white', padding: '10px 14px', borderRadius: 8, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', zIndex: 9999 }}>
          {toastMessage}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <Title>Startups com até 5 anos e até R$1MM/ano</Title>
        <Subtitle>Escolha o plano que melhor se adequa à sua empresa</Subtitle>

        <PlansContainer>
          {/* Plano Anual */}
          <PlanCard selected={selectedPlan === 'monthly'}>
            <PlanHeader>
              <PlanType>Plano Anual</PlanType>
              <PlanInstallment>12x R$ 99,90</PlanInstallment>
              <PlanTotal>Total: R$ 1.198,80/ano</PlanTotal>
            </PlanHeader>

            <BenefitsList>
              {benefits.map((benefit, index) => (
                <BenefitItem key={index}>{benefit}</BenefitItem>
              ))}
            </BenefitsList>

            <SelectButton
              type="button"
              selected={selectedPlan === 'monthly'}
              onClick={() => handleSelectPlan('monthly')}
              disabled={isProcessing}
            >
              {isProcessing && selectedPlan === 'monthly' ? 'Processando...' : selectedPlan === 'monthly' ? 'Plano Selecionado' : 'Selecionar Plano'}
            </SelectButton>
          </PlanCard>

          {/* Plano Anual à Vista */}
          <PlanCard highlight selected={selectedPlan === 'annual'}>
            <BestOfferBadge>🔥 Melhor Oferta</BestOfferBadge>
            <PlanHeader>
              <PlanType>Plano Anual</PlanType>
              <PlanPrice>R$ 990,00</PlanPrice>
              <PlanTotal>Plano Anual à vista</PlanTotal>
            </PlanHeader>

            <BenefitsList>
              {benefits.map((benefit, index) => (
                <BenefitItem key={index}>{benefit}</BenefitItem>
              ))}
            </BenefitsList>

            <SelectButton
              type="button"
              selected={selectedPlan === 'annual'}
              onClick={() => handleSelectPlan('annual')}
              disabled={isProcessing}
            >
              {isProcessing && selectedPlan === 'annual' ? 'Processando...' : selectedPlan === 'annual' ? 'Plano Selecionado' : 'Selecionar Plano'}
            </SelectButton>
          </PlanCard>
        </PlansContainer>

        <ButtonGroup>
          <Button type="button" onClick={prevStep} disabled={isProcessing}>
            Voltar
          </Button>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default StartupPlansScreen;
